import { useRef, useEffect, useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { parseBackgroundVideo } from '../utils/heroVideoUtils';

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Media',
  fields: {
    video_url: {
      label: 'Background Video URL',
      type: 'url',
      defaultField: 'custom_property_featured_video',
      description: 'Optional. Vimeo, YouTube, or direct MP4/WebM. Falls back to image when empty.',
    },
    image: {
      label: 'Fallback / Poster Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const logoDef: SlotGroupDefinition = {
  label: 'Hero Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// Three bottom "quick link" cards. Each card is a single clickable tile with a
// heading, a short blurb, and an arrow affordance.
const card1Def: SlotGroupDefinition = {
  label: 'Card · Amenities',
  fields: {
    heading: { label: 'Heading', type: 'text', defaultValue: 'Cool Amenities' },
    text: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Explore the resort-style pool, fitness center, dog park and more.',
    },
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card2Def: SlotGroupDefinition = {
  label: 'Card · Rooms',
  fields: {
    heading: { label: 'Heading', type: 'text', defaultValue: 'Luxury Rooms' },
    text: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Elevate your everyday living experience.',
    },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card3Def: SlotGroupDefinition = {
  label: 'Card · Apply',
  fields: {
    heading: { label: 'Heading', type: 'text', defaultValue: 'Apply and Save' },
    text: {
      label: 'Description',
      type: 'text',
      defaultValue: 'Save on rent, plus waived fees for a limited time.',
    },
    url: { label: 'URL', type: 'url', defaultValue: '/apply-now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function ArrowIcon() {
  return (
    <svg viewBox="0 0 40 16" fill="none" aria-hidden="true" className="hero-card-arrow">
      <path d="M0 8h37M30 1l7 7-7 7" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

interface CardLink {
  heading?: string;
  text?: string;
  url?: string;
  _target?: string;
}

function CardBody({
  card,
  defaultUrl,
  defaultHeading,
  defaultText,
}: {
  card: CardLink;
  defaultUrl: string;
  defaultHeading: string;
  defaultText: string;
}) {
  return (
    <a
      href={card.url || defaultUrl}
      target={card._target || '_self'}
      className="hero-card"
    >
      <div className="hero-card-head">
        <span className="hero-card-heading">{card.heading || defaultHeading}</span>
        <ArrowIcon />
      </div>
      <span className="hero-card-text">{card.text || defaultText}</span>
    </a>
  );
}

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const logo = useSlotGroup('hero.logo', logoDef);
  const card1 = useSlotGroup('hero.card_1', card1Def);
  const card2 = useSlotGroup('hero.card_2', card2Def);
  const card3 = useSlotGroup('hero.card_3', card3Def);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const logoImage = logo.image || monoWhiteLogo;

  // Parallax refs (image-only backgrounds)
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const videoInfo = useMemo(
    () => parseBackgroundVideo(bgImage.video_url),
    [bgImage.video_url],
  );
  const showVideoBackground = !!videoInfo && !prefersReducedMotion;

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  useEffect(() => {
    setVideoReady(false);
    setIframeLoaded(false);
  }, [videoInfo?.src, videoInfo?.type]);

  useEffect(() => {
    if (!showVideoBackground || videoInfo?.type !== 'direct') return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => setVideoReady(true);
    video.addEventListener('canplay', onCanPlay);
    if (video.readyState >= 3) setVideoReady(true);

    return () => video.removeEventListener('canplay', onCanPlay);
  }, [showVideoBackground, videoInfo]);

  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="raleway"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,400;0,700;1,400;1,700&display=swap';
    link.setAttribute('data-vibe-font', 'raleway');
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    if (showVideoBackground) return;
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, [showVideoBackground]);

  return (
    <section ref={sectionRef} className="hero-section relative w-full overflow-hidden">
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        {showVideoBackground && videoInfo ? (
          <div className="hero-bg-media absolute inset-0 bg-[var(--vibe-color-text)]">
            {bgImage.image && (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  videoInfo.type === 'embed'
                    ? iframeLoaded
                      ? 'opacity-0'
                      : 'opacity-100'
                    : videoReady
                      ? 'opacity-0'
                      : 'opacity-100'
                }`}
              />
            )}
            {videoInfo.type === 'embed' ? (
              <iframe
                src={videoInfo.src}
                className={`hero-video-iframe absolute inset-0 h-full w-full transition-opacity duration-1000 ${
                  iframeLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                allow="autoplay; fullscreen"
                frameBorder="0"
                onLoad={() => setIframeLoaded(true)}
                title="Background video"
              />
            ) : (
              <video
                ref={videoRef}
                src={videoInfo.src}
                autoPlay
                muted
                loop
                playsInline
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${
                  videoReady ? 'opacity-100' : 'opacity-0'
                }`}
              />
            )}
          </div>
        ) : (
          /* Parallax + Ken Burns animated background */
          <div
            ref={imgWrapRef}
            className="hero-bg-media absolute h-[140%]"
            style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}
          >
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="h-full w-full object-cover"
                style={{
                  animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                  willChange: 'transform',
                  transformOrigin: 'bottom right',
                  imageRendering: 'high-quality' as any,
                }}
              />
            ) : (
              <div className="h-full w-full bg-[var(--vibe-color-text)]" />
            )}
          </div>
        )}
      </EditableSlotGroup>

      {/* Primary brand tint + dark shade for text contrast */}
      <div className="hero-tint absolute inset-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/15 to-black/20" />

      {/* Content — centered stack with the quick-link cards pinned to the bottom. */}
      <div className="hero-content relative z-10 flex h-full flex-col px-6 sm:px-8 text-center text-white">
        <div className="hero-center flex flex-1 flex-col items-center justify-end">
          {logoImage ? (
            <EditableSlotGroup groupPath="hero.logo" definition={logoDef} className="hero-entrance hero-entrance-1">
              <VibesitesImage
                url={logoImage}
                alt={logo.alt || 'Community Logo'}
                className="hero-logo"
              />
            </EditableSlotGroup>
          ) : (
            <EditableSlotGroup groupPath="hero.logo" definition={logoDef} className="hidden" style={{ display: 'none' }}>
              <span />
            </EditableSlotGroup>
          )}

          <RichTextContent
            path="hero.title"
            label="Title"
            defaultValue="<p>The Ultimate Student Living Experience</p>"
            as="h1"
            className="hero-entrance hero-entrance-2 hero-title"
          />
        </div>

        <div className="hero-cards hero-entrance hero-entrance-3">
          <EditableSlotGroup groupPath="hero.card_1" definition={card1Def} className="hero-card-cell">
            <CardBody
              card={card1}
              defaultUrl="/amenities"
              defaultHeading="Cool Amenities"
              defaultText="Explore the resort-style pool, fitness center, dog park and more."
            />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.card_2" definition={card2Def} className="hero-card-cell">
            <CardBody
              card={card2}
              defaultUrl="/floor-plans"
              defaultHeading="Luxury Rooms"
              defaultText="Elevate your everyday living experience."
            />
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="hero.card_3" definition={card3Def} className="hero-card-cell">
            <CardBody
              card={card3}
              defaultUrl="/apply-now"
              defaultHeading="Apply and Save"
              defaultText="Save on rent, plus waived fees for a limited time."
            />
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.15s; }
        .hero-entrance-2 { animation-delay: 0.35s; }
        .hero-entrance-3 { animation-delay: 0.55s; }

        /* Full viewport height + a bit extra so more of the background photo
           shows above the content stack (which is pinned to the bottom). */
        .hero-section {
          isolation: isolate;
          height: 115vh;
          height: 115svh;
          height: 115dvh;
          min-height: 115vh;
          min-height: 115svh;
          min-height: 115dvh;
        }

        /* Brand-colored wash over the photo (matches the reference hero tint). */
        .hero-tint {
          background: color-mix(in srgb, var(--vibe-color-primary) 40%, transparent);
        }

        /* Cover-fit Vimeo / YouTube iframe backgrounds. */
        .hero-video-iframe {
          border: 0;
          pointer-events: none;
          width: 100vw;
          height: 56.25vw;
          min-height: 100%;
          min-width: 177.78vh;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        /* Leave room for the fixed floating navbar at the top and the cards
           at the bottom. */
        .hero-content {
          padding-top: calc(var(--offer-banner-height, 0px) + 7rem);
          padding-bottom: 2rem;
        }
        @media (min-width: 768px) {
          .hero-content {
            padding-bottom: 2.5rem;
          }
        }

        .hero-logo {
          width: min(48vw, 13rem);
          height: auto;
          max-height: 7.5rem;
          object-fit: contain;
          margin: 0 auto;
        }
        @media (min-width: 768px) {
          .hero-logo { width: auto; max-height: 12rem; max-width: 22rem; }
        }
        .hero-title {
          font-family: "Raleway", sans-serif;
          font-size: 26px;
          font-weight: 700;
          line-height: 1.05;
          letter-spacing: 0;
          color: #fff;
          text-shadow: 0 2px 24px rgba(0, 0, 0, 0.25);
          margin: 1.25rem auto 1.25rem;
          /* Sized so the default title "The Ultimate Student Living
             Experience" wraps onto exactly two lines. */
          max-width: 22ch;
        }
        .hero-title p,
        .hero-title span {
          font: inherit;
          color: inherit;
          text-shadow: inherit;
          margin: 0;
        }
        @media (min-width: 768px) {
          .hero-title { font-size: 42px; }
        }
        @media (min-width: 1024px) {
          .hero-title { font-size: 50px; }
        }

        /* ================================================================ */
        /* Bottom quick-link cards                                         */
        /* ================================================================ */
        .hero-cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.75rem;
          width: 100%;
          max-width: 78rem;
          margin: 2rem auto 0;
        }
        @media (min-width: 768px) {
          .hero-cards {
            grid-template-columns: repeat(3, 1fr);
            gap: 1rem;
            margin-top: 3rem;
          }
        }
        /* Tablet: let the three cards extend a bit past .hero-content's
           inline padding so they fill more of the row (matches the wider
           look in the reference). Desktop keeps its centered 78rem max. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-cards {
            width: calc(100% + 2rem);
            max-width: none;
            margin-left: -1rem;
            margin-right: -1rem;
          }
        }
        .hero-card-cell {
          display: flex;
        }
        .hero-card {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          width: 100%;
          padding: 1.1rem 1.35rem;
          text-align: left;
          text-decoration: none;
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.45);
          border-radius: 0.5rem;
          background: rgba(0, 0, 0, 0.18);
          backdrop-filter: blur(2px);
          -webkit-backdrop-filter: blur(2px);
          transition: background 0.3s ease, border-color 0.3s ease, transform 0.3s ease;
        }
        .hero-card:hover {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: #fff;
          transform: translateY(-2px);
        }
        .hero-card:hover .hero-card-heading,
        .hero-card:hover .hero-card-text {
          color: #fff;
        }
        .hero-card:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 3px;
        }
        .hero-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .hero-card-heading {
          font-family: "Raleway", sans-serif;
          font-size: 1.05rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          line-height: 1.1;
          white-space: nowrap;
        }
        @media (min-width: 1024px) {
          .hero-card-heading { font-size: 1.15rem; }
        }
        /* Tablet: tighten the heading↔arrow gap a touch so the single-line
           heading has room next to the fixed-width arrow. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .hero-card-head { gap: 0.5rem; }
        }
        .hero-card-arrow {
          width: 2.25rem;
          height: 0.9rem;
          flex-shrink: 0;
          transition: transform 0.3s ease;
        }
        .hero-card:hover .hero-card-arrow {
          transform: translateX(4px);
        }
        .hero-card-text {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.4;
          color: rgba(255, 255, 255, 0.88);
        }

        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
          .hero-card:hover { transform: none; }
          .hero-card:hover .hero-card-arrow { transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>The Ultimate Student Living Experience</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.logo': logoDef,
    'hero.card_1': card1Def,
    'hero.card_2': card2Def,
    'hero.card_3': card3Def,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
